<template>
  <!-- Toolbar -->
  <div class="tw-flex tw-items-center tw-justify-between tw-mb-2 tw-gap-2 tw-flex-wrap">
    <div class="tw-flex tw-items-center tw-gap-2">
      <FormField v-model="search" type="text" :hide-label="true" :hide-errors="true"
        :placeholder="$t('subscriptions.users.search', 'Search by name or email...')"
        left-icon="solar:magnifer-linear" @input="onSearch" class="tw-bg-white tw-rounded-md tw-min-w-[300px]" />
      <FormSelect v-model="filters.status" :options="statusOptions" class="tw-bg-white tw-rounded-md tw-min-w-[150px]" />
      <FormSelect v-model="filters.role" :options="roleFilterOptions" class="tw-bg-white tw-rounded-md tw-min-w-[150px]" />
    </div>
    <a-button intent="gradient" left-icon="solar:add-circle-bold-duotone" @click="openGrant">
      {{ $t('subscriptions.users.grant', 'Grant subscription') }}
    </a-button>
  </div>

  <!-- Table -->
  <div class="tw-bg-white tw-rounded-lg tw-shadow-smx tw-border tw-overflow-hidden tw-border-gray-200">
  <div class="tw-rounded tw-overflow-auto tw-border tw-border-solid tw-border-gray-100">
    <table class="tw-min-w-full tw-leading-normal">
      <thead>
        <tr>
          <th v-for="c in columns" :key="c.label" :class="c.classes"
            class="tw-px-5 last:tw-border-e-0 tw-whitespace-nowrap tw-border-e tw-py-3 tw-border-b tw-border-gray-100 tw-bg-gray-50 tw-text-start tw-text-[12px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider">
            {{ $t(c.label, c.fallback) }}
          </th>
        </tr>
      </thead>

      <tbody v-if="loading">
        <tr v-for="i in 10" :key="i" class="tw-border-b tw-border-solid tw-border-gray-100 last:tw-border-b-0 tw-group even:tw-bg-gray-50/50">
          <td v-for="c in columns" :key="c.label" class="tw-px-2 tw-py-3">
            <div class="tw-w-full tw-py-3 tw-bg-gray-50 group-even:tw-bg-white tw-rounded tw-min-w-[30px] tw-animate-pulse"></div>
          </td>
        </tr>
      </tbody>

      <tbody v-else>
        <template v-if="items.length">
          <tr v-for="s in items" :key="s.id" class="tw-border-b tw-border-solid tw-border-gray-100 last:tw-border-b-0 tw-group even:tw-bg-gray-50/50 hover:tw-bg-primary-50/60 tw-duration-100 tw-text-sm">
            <td class="tw-px-5 tw-py-3">
              <div class="tw-font-semibold tw-text-gray-800">{{ s.user?.name }}</div>
              <div class="tw-text-xs tw-text-gray-400">{{ s.user?.email }}</div>
            </td>
            <td class="tw-px-5 tw-py-3 tw-capitalize">{{ s.role }}</td>
            <td class="tw-px-5 tw-py-3 tw-whitespace-nowrap">{{ formatDate(s.ends_at) }}</td>
            <td class="tw-px-5 tw-py-3 tw-capitalize">{{ s.source }}</td>
            <td class="tw-px-5 tw-py-3">
              <span :class="statusClass(s.status)" class="tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-semibold tw-capitalize">{{ statusLabel(s.status) }}</span>
            </td>
            <td class="tw-px-5 tw-py-3">
              <div class="tw-flex tw-justify-end tw-gap-2">
                <a-button
                  intent="secondary"
                  left-icon="solar:clock-circle-bold-duotone"
                  class="!tw-min-h-[32px] !tw-py-1 !tw-text-xs"
                  @click="openExtend(s)"
                >{{ $t('subscriptions.users.extend', 'Extend') }}</a-button>
                <a-button
                  v-if="s.status === 'active'"
                  intent="danger"
                  left-icon="solar:forbidden-circle-bold-duotone"
                  class="!tw-min-h-[32px] !tw-py-1 !tw-text-xs"
                  @click="revoke(s)"
                >{{ $t('subscriptions.users.disable', 'Disable') }}</a-button>
              </div>
            </td>
          </tr>
        </template>
        <template v-else>
          <tr>
            <td :colspan="columns.length">
              <div class="tw-p-5 tw-bg-white tw-min-h-[250px] tw-text-gray-600 tw-flex tw-items-center tw-justify-center tw-text-lg">
                <p>{{ $t('subscriptions.users.empty', 'No subscriptions yet.') }}</p>
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
  </div>

  <div class="tw-mt-2">
    <TablePagination v-if="items.length" :has-next="!!pagination.next_page_url" :has-previous="!!pagination.prev_page_url" @previous="onPrevious" @next="onNext" />
  </div>

  <!-- Grant / Extend popup (global high z-index) -->
  <a-popup :visible="modal.open" @cancel="closeModal">
    <div class="tw-mx-auto tw-w-[95%] md:tw-max-w-[520px] tw-overflow-hidden tw-h-fit tw-bg-white tw-border tw-border-solid tw-border-gray-300 tw-shadow-md tw-my-5 tw-rounded">
      <!-- Header -->
      <div class="tw-p-4 tw-text-lg tw-border-b tw-border-solid tw-font-medium tw-flex tw-text-gray-600 tw-items-center tw-gap-3 tw-bg-gray-50">
        <icon :icon="modal.mode === 'grant' ? 'solar:ticket-star-bold-duotone' : 'solar:refresh-circle-bold-duotone'" class="tw-text-primary-500 tw-text-2xl" />
        {{ modal.mode === 'grant' ? $t('subscriptions.users.grant', 'Grant subscription') : $t('subscriptions.users.extend', 'Extend subscription') }}
      </div>

      <!-- Body -->
      <div class="tw-p-5 tw-bg-white tw-space-y-4">
        <p v-if="modal.mode === 'extend'" class="tw-text-sm tw-text-gray-500">
          {{ $t('subscriptions.users.extend-hint', 'Add more time to') }} <span class="tw-font-semibold tw-text-gray-700">{{ modal.label }}</span>.
        </p>

        <FormMultiSelect
          v-if="modal.mode === 'grant'"
          v-model="modal.users"
          :options="userOptions"
          :label="$t('fields.user', 'Users')"
          :placeholder="$t('subscriptions.users.select-users', 'Search and select users')"
          left-icon="solar:users-group-rounded-bold-duotone"
          :error="modal.errors.user_ids"
        />

        <FormVueSelect
          v-if="modal.mode === 'grant'"
          v-model="modal.role"
          :options="roleOptions"
          option-label="name"
          option-value="id"
          :clearable="false"
          :label="$t('subscriptions.plans.role', 'Role')"
          :placeholder="$t('subscriptions.users.select-role', 'Select role')"
          left-icon="solar:shield-user-bold-duotone"
          :error="modal.errors.role"
        />

        <div class="tw-flex tw-gap-3">
          <div class="tw-flex-1">
            <label class="tw-block tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-2">{{ $t('subscriptions.users.amount', 'Amount') }}</label>
            <input type="number" min="1" max="120" v-model.number="modal.duration_count"
              class="tw-w-full tw-h-[40px] tw-border tw-border-solid tw-border-gray-200 tw-rounded-md tw-px-3 tw-text-sm focus:tw-border-primary-500 tw-outline-none" />
          </div>
          <div class="tw-flex-1">
            <FormVueSelect v-model="modal.duration_unit" :options="unitOptions" option-label="name" option-value="id"
              :clearable="false" :label="$t('subscriptions.users.unit', 'Unit')" />
          </div>
        </div>

        <!-- Notify the user by email (extend only) -->
        <label v-if="modal.mode === 'extend'" class="tw-flex tw-items-center tw-gap-3 tw-cursor-pointer tw-select-none tw-group tw-mt-1">
          <input type="checkbox" v-model="modal.notify" class="tw-w-4 tw-h-4 tw-rounded tw-border-gray-300 tw-text-primary-600 focus:tw-ring-primary-500 tw-cursor-pointer" />
          <span class="tw-text-sm tw-text-gray-700 group-hover:tw-text-gray-900">
            {{ $t('subscriptions.users.notify', 'Email the user to let them know') }}
          </span>
        </label>
      </div>

      <!-- Footer -->
      <div class="tw-p-4 tw-border-t tw-justify-end tw-border-solid tw-flex tw-items-center tw-gap-3 tw-bg-gray-50">
        <a-button :disabled="modal.saving" intent="simple" class="tw-min-w-[120px]" @click="closeModal">{{ $t('buttons.cancel', 'Cancel') }}</a-button>
        <a-button :loading="modal.saving" :disabled="modal.saving" class="tw-min-w-[120px]" @click="submit">
          {{ modal.mode === 'grant' ? $t('subscriptions.users.grant-btn', 'Grant') : $t('buttons.save', 'Save') }}
        </a-button>
      </div>
    </div>
  </a-popup>
</template>

<script setup>
import { ref, reactive, onMounted, watch } from 'vue';
import FormField from '@/components/form/FormField.vue';
import FormSelect from '@/components/form/FormSelect.vue';
import FormVueSelect from '@/components/form/FormVueSelect.vue';
import FormMultiSelect from '@/components/form/FormMultiSelect.vue';
import TablePagination from '@/components/table/TablePagination';
import { forms } from '@/api/app';
import subscriptionsApi from '@/api/subscriptions';
import useAlert from '@/composables/useAlert';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const items = ref([]);
const loading = ref(false);
const search = ref('');
const pagination = reactive({ current_page: 1, last_page: 1, total: 0, next_page_url: null, prev_page_url: null });
const filters = reactive({ status: 'all', role: 'all' });

const columns = [
  { label: 'fields.user', fallback: 'User', classes: [] },
  { label: 'subscriptions.plans.role', fallback: 'Role', classes: ['tw-w-[120px]'] },
  { label: 'subscriptions.users.ends', fallback: 'Ends', classes: ['tw-w-[130px]'] },
  { label: 'subscriptions.users.source', fallback: 'Source', classes: ['tw-w-[120px]'] },
  { label: 'subscriptions.plans.status', fallback: 'Status', classes: ['tw-w-[110px]'] },
  { label: 'common.actions', fallback: 'Actions', classes: ['tw-w-[140px] tw-text-end'] },
];

const roles = ['attendee', 'exhibitor', 'buyer', 'sponsor', 'speaker'];
const roleOptions = roles.map(r => ({ id: r, name: r.charAt(0).toUpperCase() + r.slice(1) }));
const roleFilterOptions = [{ id: 'all', name: t('common.all-roles', 'All roles') }, ...roleOptions];
const statusOptions = [
  { id: 'all', name: t('subscriptions.users.all-status', 'All statuses') },
  { id: 'active', name: t('common.active', 'Active') },
  { id: 'expired', name: t('subscriptions.users.expired', 'Expired') },
  { id: 'cancelled', name: t('subscriptions.users.disabled', 'Disabled') },
];
const unitOptions = [
  { id: 'day', name: t('subscriptions.unit.day', 'Day(s)') },
  { id: 'week', name: t('subscriptions.unit.week', 'Week(s)') },
  { id: 'month', name: t('subscriptions.unit.month', 'Month(s)') },
  { id: 'year', name: t('subscriptions.unit.year', 'Year(s)') },
];
const userOptions = ref([]);

const modal = reactive({ open: false, mode: 'grant', saving: false, id: null, label: '', users: [], role: 'attendee', duration_count: 1, duration_unit: 'month', notify: false, errors: {} });

let searchTimer = null;
const onSearch = () => { clearTimeout(searchTimer); searchTimer = setTimeout(() => load(1), 500); };

const load = async (page = 1) => {
  loading.value = true;
  items.value = [];
  try {
    const res = await subscriptionsApi.admin.users({ page, status: filters.status, role: filters.role, search: search.value || undefined });
    const d = res.data.data;
    items.value = d.data || [];
    Object.assign(pagination, { current_page: d.current_page, last_page: d.last_page, total: d.total, next_page_url: d.next_page_url, prev_page_url: d.prev_page_url });
  } catch (e) {
    useAlert(t('errors.something-wrong-happened'), 'danger');
  } finally {
    loading.value = false;
  }
};

const onNext = () => { if (pagination.current_page < pagination.last_page) load(pagination.current_page + 1); };
const onPrevious = () => { if (pagination.current_page > 1) load(pagination.current_page - 1); };

const loadUsers = async () => {
  try {
    const res = await forms.users([]);
    userOptions.value = Array.isArray(res.data) ? res.data : (res.data.data || []);
  } catch (e) { /* non-fatal */ }
};

const openGrant = () => {
  Object.assign(modal, { open: true, mode: 'grant', id: null, label: '', users: [], role: 'attendee', duration_count: 1, duration_unit: 'month', errors: {} });
};
const openExtend = (s) => {
  Object.assign(modal, { open: true, mode: 'extend', id: s.id, label: s.user?.name || '', duration_count: 1, duration_unit: 'month', notify: false, errors: {} });
};
const closeModal = () => { modal.open = false; };

const submit = async () => {
  modal.errors = {};
  if (modal.mode === 'grant' && (!modal.users || !modal.users.length)) {
    modal.errors.user_ids = t('subscriptions.users.pick-user', 'Select at least one user');
    return;
  }
  modal.saving = true;
  try {
    if (modal.mode === 'grant') {
      await subscriptionsApi.admin.grant({
        user_ids: modal.users.map(u => u.id),
        role: modal.role,
        duration_count: modal.duration_count,
        duration_unit: modal.duration_unit,
      });
    } else {
      await subscriptionsApi.admin.extend(modal.id, { duration_count: modal.duration_count, duration_unit: modal.duration_unit, notify: modal.notify });
    }
    modal.open = false;
    useAlert(t('common.saved', 'Saved'), 'success');
    load(modal.mode === 'grant' ? 1 : pagination.current_page);
  } catch (e) {
    const msg = e?.response?.data?.errors ? Object.values(e.response.data.errors)[0][0] : t('errors.something-wrong-happened');
    useAlert(msg, 'danger');
  } finally {
    modal.saving = false;
  }
};

const revoke = async (s) => {
  if (!confirm(t('subscriptions.users.confirm-revoke', 'Disable this subscription now?'))) return;
  try { await subscriptionsApi.admin.revoke(s.id); useAlert(t('common.saved', 'Saved'), 'success'); load(pagination.current_page); }
  catch (e) { useAlert(t('errors.something-wrong-happened'), 'danger'); }
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
const statusLabel = (s) => s === 'cancelled' ? t('subscriptions.users.disabled', 'Disabled') : s;
const statusClass = (s) => ({
  active: 'tw-bg-green-100 tw-text-green-700',
  expired: 'tw-bg-gray-100 tw-text-gray-500',
  cancelled: 'tw-bg-red-100 tw-text-red-600',
}[s] || 'tw-bg-gray-100 tw-text-gray-500');

let filterTimer = null;
const reloadOnFilter = () => { clearTimeout(filterTimer); filterTimer = setTimeout(() => load(1), 200); };

watch(() => [filters.status, filters.role], reloadOnFilter);

onMounted(() => { load(); loadUsers(); });
</script>
